/**
 * D-EZ-110 — HALL OF FAME NIGHT.
 *
 * `hallOfFame()` (history/awards.ts) decides who goes in, on bars written down
 * where they can be argued with. This gives each inductee his night: the case
 * that put him there, his numbers, the rivalries that defined him, what the
 * booth's analyst says about him, what his greatest rival says, and — for one
 * of the league's own drivers — a few words of his own. The analyst speaks
 * only to the record: the numbers, the titles, the worst moment of the
 * rivalry, all slots.
 *
 * Inducted the season after he leaves (`inductedSeason`), so Hall of Fame
 * night is part of the winter that opens that season.
 */

import { CAST } from '../booth/cast.ts';
import { HALL_OF_FAME_RULES, hallOfFame, hallOfFameCase, type CareerTotals } from '../history/index.ts';
import { dollars } from './text.ts';
import { isRealDriver } from '../story/hometown.ts';
import { chapterContext, seasonsThrough, type ChapterSeason } from './league.ts';
import { careerRetrospective, listOf } from './farewell.ts';
import { count, headlineNamer, plural, rotor, tell, tellOne, winterQuote, type Said, type Told } from './text.ts';

export interface Inductee {
  driverId: string;
  name: string;
  real: boolean;
  inductedSeason: number;
  /** The bar he cleared, in words (`hallOfFameCase`). */
  case: string;
  numbers: { seasons: number; starts: number; wins: number; championships: number; poles: number; top5: number; top10: number; winningsMb: number; titleSeasons: number[] };
  rivals: { otherId: string; otherName: string; worst: string | null; ahead: number; behind: number }[];
  /** The introduction, read off the record. */
  intro: Told[];
  analyst: { name: string; seat: string; words: Told };
  /** His greatest rival's words — only a fictional rival speaks. */
  rival: Said | null;
  /** His own words — only a fictional inductee speaks. */
  speech: Said | null;
}

export interface HallOfFameNight {
  inductedSeason: number;
  /** The winter he retired in: the season before. */
  afterSeason: number;
  inductees: Inductee[];
  intro: Told;
}

/**
 * The class inducted at the start of `inductedSeason` (a driver lost in the car
 * goes in the season he was lost — `hallOfFame` already says so). Reads the
 * league through the winter before that season only.
 */
export function hallOfFameNight(input: readonly ChapterSeason[], inductedSeason: number): HallOfFameNight | null {
  const seasons = seasonsThrough(input, inductedSeason - 1, { winter: true });
  if (seasons.length === 0) return null;
  const cx = chapterContext(seasons, input);
  const cls = hallOfFame(cx.registry, inductedSeason).filter((h) => h.inductedSeason === inductedSeason);
  if (cls.length === 0) return null;
  const used = new Set<string>();
  const namer = headlineNamer(cx.registry.all.map((e) => [e.driverId, e.name] as [string, string]));
  const introR = rotor(`hof-intro|${cx.leagueKey}|${inductedSeason}`);
  const inductees: Inductee[] = cls.map((h) => {
    const e = cx.registry.byId[h.driverId];
    const retro = careerRetrospective(seasons, h.driverId, inductedSeason - 1, cx);
    const c = e.career;
    const titleSeasons = e.bySeason.filter((l) => l.champion).map((l) => l.seasonNumber);
    const key = `hof|${cx.leagueKey}|${inductedSeason}|${h.driverId}`;
    const sur = namer(h.driverId, h.name);
    const rivals = (retro?.rivals ?? []).map((r) => ({ otherId: r.otherId, otherName: r.otherName, worst: r.worst, ahead: r.ahead, behind: r.behind }));
    const intro: Told[] = [];
    intro.push(
      introR(['{name} goes into the Hall of Fame on the strength of {why}.', 'The case for {name} made itself: {why}.', '{why} — that is the case for {name}, and it was never close.'], { name: h.name, why: caseWords(c) }),
    );
    intro.push(
      tell('{starts} starts, {wins} wins, {top10} top tens and {poles} poles{titles} — and {money} won on the race track.', {
        starts: c.starts,
        wins: c.wins,
        top10: c.top10,
        poles: c.poles,
        titles: c.championships > 0 ? `, ${c.championships} ${plural(c.championships, 'championship')}` : '',
        money: dollars(c.winningsMb),
      }),
    );
    if (titleSeasons.length > 0) intro.push(tell('Champion in {list}.', { list: listOf(titleSeasons.map((n) => `season ${n}`)) }));
    if (e.prior && (e.prior.starts ?? 0) > 0) intro.push(tell('Those numbers count the career he brought into this league ({src}).', { src: e.prior.source }));
    rivals.slice(0, 2).forEach((r, i) => {
      intro.push(
        i === 0
          ? r.worst
            ? tell('His great rivalry was {other}. The worst of it: {worst}', { other: r.otherName, worst: r.worst })
            : tell('His great rivalry was {other}.', { other: r.otherName })
          : r.worst
            ? tell('And there was {other}: {worst}', { other: r.otherName, worst: r.worst })
            : tell('And there was {other}.', { other: r.otherName }),
      );
    });
    // The analyst speaks to the record and nothing else.
    const top = rivals[0];
    const aSlots: Record<string, string | number> = { sur, wins: c.wins, starts: c.starts, titles: count(c.championships), t: plural(c.championships, 'title') };
    if (top) aSlots.rival = namer(top.otherId, top.otherName);
    const analystOptions = c.championships >= 2
      ? ['{titles} {t}. You can argue about who was quickest on a given day; you can’t argue with {titles} {t}.', 'Plenty of people win races in this sport. {sur} won championships — {titles} of them.']
      : c.wins >= 30
        ? ['{wins} wins. Think about that. {wins} times the whole field chased {sur} and nobody caught him.', 'You don’t back into {wins} wins. {sur} took every one of them.']
        : ['{wins} wins across {starts} starts. That is a career, start to finish.', 'Week in, week out, for {starts} starts. {sur} was there.'];
    if (top) analystOptions.push('Everybody will tell you about the wins. I’ll tell you about {sur} and {rival}. That was racing.');
    const analyst = { name: CAST.analyst.name, seat: CAST.analyst.seat, words: tellOne(`${key}|an`, analystOptions, aSlots) };
    const rivalSays = top && !isRealDriver(top.otherId) ? winterQuote('tribute', top.otherId, top.otherName, cx.people, { other: sur }, `${inductedSeason}|${h.driverId}`, used) : null;
    const speech = winterQuote('inducted', h.driverId, h.name, cx.people, {}, `${inductedSeason}`, used);
    return {
      driverId: h.driverId,
      name: h.name,
      real: isRealDriver(h.driverId),
      inductedSeason,
      case: hallOfFameCase(e) ?? h.citation,
      numbers: { seasons: c.seasons, starts: c.starts, wins: c.wins, championships: c.championships, poles: c.poles, top5: c.top5, top10: c.top10, winningsMb: c.winningsMb, titleSeasons },
      rivals,
      intro,
      analyst,
      rival: rivalSays,
      speech,
    };
  });
  const intro = tellOne(`hof-night|${cx.leagueKey}|${inductedSeason}`, inductees.length === 1
    ? ['Before season {n} turns a wheel, the league opens the Hall of Fame doors to a new member.', 'Hall of Fame night, season {n}: one name goes on the wall.']
    : ['Hall of Fame night, season {n}: {k} names go on the wall.', 'The Hall of Fame class of season {n} is {k} strong.'], { n: inductedSeason, k: count(inductees.length) });
  return { inductedSeason, afterSeason: inductedSeason - 1, inductees, intro };
}

/** The bar he cleared, as a phrase that reads after "on the strength of". */
function caseWords(c: CareerTotals): string {
  const r = HALL_OF_FAME_RULES;
  if (c.championships >= r.titles) return `${c.championships} championships`;
  if (c.championships >= r.titlePlusWins.titles && c.wins >= r.titlePlusWins.wins) return `a championship and ${c.wins} career wins`;
  if (c.wins >= r.wins) return `${c.wins} career wins`;
  return `${c.wins} wins across ${c.starts} starts`;
}
